import React from 'react'
import {
  Button,
  Empty,
  Form,
  FormInstance,
  Input,
  Select,
  Tooltip,
  Typography
} from 'antd'
import omit from 'lodash.omit'
import {
  DeleteOutlined,
  ForkOutlined,
  QuestionCircleOutlined,
  TagsOutlined
} from '@ant-design/icons'
import {
  EProductType,
  ICharacteristic,
  IChangeVariationProduct,
  IFrontVariation,
  IFrontVariationProduct,
  ICreateUpdateVariationProduct,
  ISubCategoryCharacteristic
} from 'models/product'
import { showMessage } from 'shared/utils/showMessage'
import { fetchSubCategoryCharacteristics } from '../utils/characteristicsSync'
import {
  validateCharacteristicName,
  validateCharacteristicValue,
  capitalizeFirstLetter
} from '../utils/characteristicsValidation'
import { updateVariationCharacteristicName } from '../utils/variationUtils'
import {
  updateVariationProductNames,
  hasVariationNameChanges
} from '../utils/variationNameUtils'
import styles from '../ProductForm.module.scss'
import { ChooseVariationProductModal } from './ChooseVariationProductModal'
import { VariantProductModalForm } from './VariantProductModalForm'
import { VariationsTable } from './VariationsTable'

interface ICharacteristicsProps {
  form: FormInstance
  sellerId: string
  productId?: string
  productType?: EProductType
  firstVariationName: string | undefined
  secondVariationName: string | undefined
  addToDeletedVariations: (productId: string) => void
}

export const Characteristics: React.FC<ICharacteristicsProps> = React.memo(
  ({
    form,
    sellerId,
    productId,
    productType,
    firstVariationName,
    secondVariationName,
    addToDeletedVariations
  }) => {
    // вариативные характеристики — заголовки колонок таблицы «Вариации товара» (максимум две)
    const [axis1, setAxis1] = React.useState<string | undefined>(undefined)
    const [axis2, setAxis2] = React.useState<string | undefined>(undefined)
    // индексы строк вариаций без привязанного товара, подсвечиваемых при попытке сохранения
    const [charVariationErrors, setCharVariationErrors] = React.useState<Set<number>>(new Set())

    const [chooseProductData, setChooseProductData] =
      React.useState<IChangeVariationProduct>({
        visible: false,
        variation: null,
        variationIndex: null
      })

    const [createUpdateProductData, setCreateUpdateProductData] =
      React.useState<ICreateUpdateVariationProduct>({
        visible: false,
        type: null,
        variation: null,
        variationIndex: null
      })

    const [subCategoryCharacteristics, setSubCategoryCharacteristics] =
      React.useState<ISubCategoryCharacteristic[]>([])
    const [, setIsLoadingSubCategoryCharacteristics] = React.useState(false)
    const [isInitialLoad, setIsInitialLoad] = React.useState(true)

    const createCharacteristicName: string =
      Form.useWatch('createCharacteristicName', { form, preserve: true }) ?? ''
    const createCharacteristicValue: string =
      Form.useWatch(['createCharacteristicValues', 0], { form, preserve: true }) ?? ''

    // Ищем соответствующую характеристику подкатегории (сравнение в нижнем регистре)
    const matchingSubCategoryChar = React.useMemo(() => {
      if (!createCharacteristicName.trim()) return null
      return subCategoryCharacteristics.find(
        (char) =>
          char.name.toLowerCase() ===
          createCharacteristicName.trim().toLowerCase()
      )
    }, [createCharacteristicName, subCategoryCharacteristics])

    // полный снимок стора: строки таблицы вариаций задаются без Form.Item,
    // а getFieldsValue() без аргумента отдаёт только зарегистрированные поля
    const parentFormData = form.getFieldsValue(true)

    const characteristics: ICharacteristic[] =
      Form.useWatch('characteristics', {
        form,
        preserve: true
      }) ?? []

    const requiredCharacteristics: ICharacteristic[] =
      Form.useWatch('requiredCharacteristics', {
        form,
        preserve: true
      }) ?? []

    const optionalCharacteristics: ICharacteristic[] =
      Form.useWatch('optionalCharacteristics', {
        form,
        preserve: true
      }) ?? []

    const externalItemId: string | undefined =
      Form.useWatch('external_item_id', {
        form,
        preserve: true
      }) ?? undefined

    const isMoySkladSyncedProduct = Boolean(externalItemId)

    const variations: IFrontVariation[] =
      Form.useWatch('variations', {
        form,
        preserve: true
      }) ?? []

    const categoriesId: string[] =
      Form.useWatch('categories_id', {
        form,
        preserve: true
      }) ?? []

    const subCategoryId = categoriesId[1]

    // Строка привязана к requiredCharacteristics по индексу, а там только обязательные:
    // необязательные выводит список optionalCharacteristics, иначе каждая встаёт дважды
    const requiredSubCategoryCharacteristics = React.useMemo(
      () => subCategoryCharacteristics.filter((char) => char.required),
      [subCategoryCharacteristics]
    )

    const getSubCategoryCharacteristics = async (subCategoryId: string) => {
      try {
        setIsLoadingSubCategoryCharacteristics(true)
        const characteristics = await fetchSubCategoryCharacteristics(
          subCategoryId
        )
        setSubCategoryCharacteristics(characteristics)
        return characteristics
      } finally {
        setIsLoadingSubCategoryCharacteristics(false)
      }
    }

    // подхватываем оси таблицы из загруженного товара с вариациями
    React.useEffect(() => {
      if (firstVariationName) setAxis1((prev) => prev ?? firstVariationName)
    }, [firstVariationName])

    React.useEffect(() => {
      if (secondVariationName) setAxis2((prev) => prev ?? secondVariationName)
    }, [secondVariationName])

    // заполненные характеристики основного товара: вариативной можно выбрать только их —
    // основной товар сам является первой вариацией и должен иметь значение
    const filledParentCharacteristics = React.useMemo(
      () =>
        [
          ...requiredCharacteristics,
          ...optionalCharacteristics,
          ...characteristics
        ].filter((c) => c?.name?.trim() && c?.value?.trim()),
      [requiredCharacteristics, optionalCharacteristics, characteristics]
    )

    // смена характеристики в заголовке таблицы: переименовывает её во всех строках,
    // сбрасывая только несовместимые значения; сброс второй оси убирает её из строк
    const handleAxisChange = (
      position: 'first' | 'second',
      name: string | undefined
    ) => {
      const capitalized = name ? capitalizeFirstLetter(name.trim()) : undefined
      const currentVariations: IFrontVariation[] =
        form.getFieldValue('variations') ?? []

      if (position === 'first') {
        if (!capitalized) {
          setAxis1(undefined)
          form.setFieldValue('variations', [])
          return
        }
        setAxis1(capitalized)
        if (currentVariations.length) {
          form.setFieldValue(
            'variations',
            updateVariationCharacteristicName(
              currentVariations,
              'first',
              capitalized,
              subCategoryCharacteristics
            )
          )
        }
        return
      }

      if (!capitalized) {
        setAxis2(undefined)
        form.setFieldValue(
          'variations',
          currentVariations.map((v) =>
            omit(v, ['secondCharacteristicName', 'secondCharacteristicValue'])
          )
        )
        return
      }
      setAxis2(capitalized)
      if (currentVariations.length) {
        form.setFieldValue(
          'variations',
          updateVariationCharacteristicName(
            currentVariations,
            'second',
            capitalized,
            subCategoryCharacteristics
          )
        )
      }
    }

    const onCreateCharacteristic = () => {
      const createCharacteristicName: string =
        form.getFieldValue('createCharacteristicName')?.trim() ?? ''
      const createCharacteristicValues: string[] = form.getFieldValue(
        'createCharacteristicValues'
      )

      // Проверяем существование массива значений характеристик
      if (!createCharacteristicValues || !Array.isArray(createCharacteristicValues) || !createCharacteristicValues[0]) {
        return showMessage('error', 'Необходимо указать значение характеристики', '', 2)
      }

      // Обязательные и необязательные характеристики подкатегории лежат в своих полях,
      // но в товар уходят одним списком — дубль с ними тоже недопустим
      const existingCharacteristics = [
        ...(form.getFieldValue('characteristics') || []),
        ...(form.getFieldValue('requiredCharacteristics') || []),
        ...(form.getFieldValue('optionalCharacteristics') || [])
      ]

      // Валидация названия характеристики с проверкой дублирования (в т.ч. с вариативными осями)
      const nameError = validateCharacteristicName(
        createCharacteristicName,
        existingCharacteristics,
        axis1,
        axis2
      )
      if (nameError) {
        return showMessage('error', nameError, '', 2)
      }

      // Валидация значения характеристики
      const valueError = validateCharacteristicValue(
        createCharacteristicValues[0]
      )
      if (valueError) {
        return showMessage('error', valueError, '', 2)
      }

      const capitalizedName = capitalizeFirstLetter(createCharacteristicName)

      form.setFieldValue('characteristics', [
        ...(form.getFieldValue('characteristics') ?? []),
        {
          name: capitalizedName,
          value: createCharacteristicValues[0].trim()
        }
      ])
      form.setFieldValue('createCharacteristicName', '')
      form.setFieldValue('createCharacteristicValues', [''])
    }

    const openChooseProductModal = React.useCallback(
      (variation: IFrontVariation, variationIndex: number) => {
        closeCreateUpdateProductModal()
        setChooseProductData({
          visible: true,
          variation,
          variationIndex
        })
      },
      []
    )

    const closeChooseProductModal = React.useCallback(() => {
      setChooseProductData({
        visible: false,
        variation: null,
        variationIndex: null
      })
    }, [])

    const openUpdateProductModal = React.useCallback(
      (variation: IFrontVariation, variationIndex: number) => {
        closeChooseProductModal()

        // Упрощенная версия - передаем только базовые данные
        setCreateUpdateProductData({
          visible: true,
          type: 'update',
          variation,
          variationIndex
        })
      },
      []
    )

    const openCreateProductModal = React.useCallback(
      (variation: IFrontVariation, variationIndex: number) => {
        closeChooseProductModal()

        // Упрощенная версия - передаем только базовые данные
        setCreateUpdateProductData({
          visible: true,
          type: 'create',
          variation,
          variationIndex
        })
      },
      []
    )

    const openCreateProductModalForChoosing = React.useCallback(() => {
      closeChooseProductModal()

      if (!chooseProductData?.variation) return

      // Упрощенная версия - передаем только базовые данные
      setCreateUpdateProductData({
        visible: true,
        type: 'create',
        variation: chooseProductData?.variation,
        variationIndex: chooseProductData?.variationIndex
      })
    }, [chooseProductData])

    const closeCreateUpdateProductModal = React.useCallback(() => {
      setCreateUpdateProductData({
        visible: false,
        type: null,
        variation: null,
        variationIndex: null
      })
    }, [])

    const onSelectVariationProduct = React.useCallback(
      (
        product: IFrontVariationProduct,
        variationIndex: number,
        updatedCharacteristics?: ICharacteristic[]
      ) => {
        form.setFieldValue(
          'variations',
          variations.map((item, index) => {
            if (index === variationIndex) {
              return { ...item, product }
            }
            return item
          })
        )
        // снимаем все подсветки ошибок — пользователь заполняет вариации
        setCharVariationErrors(new Set())
        closeCreateUpdateProductModal()
        closeChooseProductModal()
      },
      [variations]
    )

    React.useEffect(() => {
      // For variation products, don't load subcategory characteristics
      // They should only show characteristics that are already set through the main product
      if (subCategoryId && productType !== EProductType.VARIATION) {
        getSubCategoryCharacteristics(subCategoryId).then((characteristics) => {
          // При первой загрузке не трогаем requiredCharacteristics - они уже установлены в ProductForm
          if (!isInitialLoad) {
            const currentRequiredCharacteristics = form.getFieldValue('requiredCharacteristics') || []
            const currentOptionalCharacteristics = form.getFieldValue('optionalCharacteristics') || []
            const currentCharacteristics = form.getFieldValue('characteristics') || []
            const allCurrentCharacteristics = [
              ...currentRequiredCharacteristics,
              ...currentOptionalCharacteristics,
              ...currentCharacteristics
            ]
            
            // Названия расходятся по регистру и пробелам — иначе значение теряется,
            // а прежняя характеристика остаётся в общем блоке вторым таким же полем
            const isSameName = (a?: string, b?: string) =>
              (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()

            // Создаем новые характеристики, сохраняя существующие значения где возможно
            const buildFromSubCategory = (required: boolean) =>
              characteristics
                .filter((char) => Boolean(char.required) === required)
                .map((char) => ({
                  name: char.name,
                  value:
                    allCurrentCharacteristics.find((existing: ICharacteristic) =>
                      isSameName(existing.name, char.name)
                    )?.value || ''
                }))

            // Устанавливаем обновленные обязательные характеристики
            form.setFieldValue('requiredCharacteristics', buildFromSubCategory(true))
            form.setFieldValue('optionalCharacteristics', buildFromSubCategory(false))
            form.setFieldValue(
              'characteristics',
              currentCharacteristics.filter(
                (existing: ICharacteristic) =>
                  !characteristics.some((char) =>
                    isSameName(existing.name, char.name)
                  )
              )
            )
          }
        })
      } else {
        setSubCategoryCharacteristics([])
        // Очищаем обязательные характеристики если нет подкатегории (но не при первой загрузке)
        if (productType !== EProductType.VARIATION && !isInitialLoad) {
          form.setFieldValue('requiredCharacteristics', [])
          form.setFieldValue('optionalCharacteristics', [])
        }
      }
      
      // После первого выполнения эффекта помечаем что это больше не первая загрузка
      if (isInitialLoad) {
        setIsInitialLoad(false)
      }
    }, [subCategoryId, productType, isInitialLoad])

    // Обновляем названия товаров-вариаций при изменении характеристик
    React.useEffect(() => {
      const updatedVariations = updateVariationProductNames(
        variations,
        parentFormData?.name || ''
      )

      // Обновляем variations только если есть изменения
      if (hasVariationNameChanges(variations, updatedVariations)) {
        form.setFieldValue('variations', updatedVariations)
      }
    }, [variations, parentFormData?.name])

    return (
      <>
        {/* Таблица вариаций: вариативные характеристики задаются в заголовках колонок */}
        {productType !== EProductType.VARIATION && (
          <div className={styles.subSection}>
            <Typography.Title
              level={4}
              className={styles.sectionSubTitle}
              style={{ marginBottom: 18 }}
            >
              Вариации товара:
              <Tooltip
                overlayStyle={{ maxWidth: 800 }}
                title={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span>
                      Вариации — один и тот же товар в разных исполнениях,
                      покупатель переключает их на одной карточке.
                    </span>
                    <span>
                      «Характеристика 1» — обязательна (например, «Размер» или
                      «Цвет»).
                    </span>
                    <span>
                      «Характеристика 2» — дополнительная, если товар
                      различается сразу по двум признакам («Размер» + «Цвет»).
                    </span>
                    <span>
                      Пример: попона в размерах Cob/Full и двух цветах =
                      четыре строки (Cob + Чёрный, Cob + Мока, Full + Чёрный,
                      Full + Мока).
                    </span>
                    <span>
                      Основной товар является первой вариацией, сперва
                      заполните его характеристики.
                    </span>
                  </div>
                }
              >
                <QuestionCircleOutlined
                  style={{
                    fontSize: 16,
                    color: 'rgba(0, 0, 0, 0.45)',
                    marginLeft: 8,
                    cursor: 'help'
                  }}
                />
              </Tooltip>
            </Typography.Title>
            <VariationsTable
              form={form}
              variations={variations}
              axis1={axis1}
              axis2={axis2}
              onAxisChange={handleAxisChange}
              subCategoryCharacteristics={subCategoryCharacteristics}
              parentCharacteristics={filledParentCharacteristics}
              parentName={parentFormData?.name}
              rowErrors={charVariationErrors}
              openUpdateProductModal={openUpdateProductModal}
              openCreateProductModal={openCreateProductModal}
              openChooseProductModal={openChooseProductModal}
              addToDeletedVariations={addToDeletedVariations}
            />
          </div>
        )}
        <div className={styles.subSection}>
          <Typography.Title
            level={4}
            className={styles.sectionSubTitle}
            style={{ marginBottom: 18 }}
          >
            Общие характеристики:
          </Typography.Title>

          {!characteristics.length &&
          !variations.length &&
          !subCategoryCharacteristics.length &&
          !optionalCharacteristics.length &&
          !requiredCharacteristics.length ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<></>} />
            ) : (
              <div className={styles.characteristicsWrapper}>
                {/* Обязательные характеристики */}
                {productType === EProductType.VARIATION
                  ? // Для вариаций показываем только установленные через основной товар характеристики
                  requiredCharacteristics.map((characteristic, index) => (
                    <div
                      key={`variation-required-${index}`}
                      className={styles.characteristicRow}
                    >
                      <TagsOutlined className={styles.characteristicRowIcon} />
                      <Form.Item
                        name={['requiredCharacteristics', index, 'name']}
                        className={styles.characteristicRowField}
                        hasFeedback
                      >
                        <Input
                          size='middle'
                          placeholder='Название характеристики'
                          disabled
                          style={{
                            color: 'rgba(0, 0, 0, 0.45)',
                            backgroundColor: '#f5f5f5'
                          }}
                          suffix={
                            !isMoySkladSyncedProduct ? (
                              <span style={{ color: '#ff4d4f' }}>*</span>
                            ) : undefined
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name={['requiredCharacteristics', index, 'value']}
                        className={styles.characteristicRowField}
                        rules={isMoySkladSyncedProduct ? [] : [
                          {
                            required: true,
                            message: `Заполните ${characteristic.name}`
                          }
                        ]}
                        hasFeedback
                      >
                        <Input
                          size='middle'
                          placeholder={`Введите ${characteristic.name.toLowerCase()}`}
                          disabled
                          style={{
                            color: 'rgba(0, 0, 0, 0.45)',
                            backgroundColor: '#f5f5f5'
                          }}
                        />
                      </Form.Item>
                      <div className={styles.characteristicRowSpacer} />
                    </div>
                  ))
                  : // Для обычных товаров показываем обязательные характеристики подкатегории;
                  // значения основного товара — одна из комбинаций на карточке
                  requiredSubCategoryCharacteristics.map((characteristic, index) => (
                    <React.Fragment key={`required-${index}`}>
                      <div
                        className={styles.characteristicRow}
                      >
                        <TagsOutlined className={styles.characteristicRowIcon} />
                        <Form.Item
                          name={['requiredCharacteristics', index, 'name']}
                          className={styles.characteristicRowField}
                          initialValue={characteristic.name}
                          hasFeedback
                        >
                          <Input
                            size='middle'
                            placeholder='Название характеристики'
                            disabled
                            style={{
                              color: 'rgba(0, 0, 0, 0.45)',
                              backgroundColor: '#f5f5f5'
                            }}
                            suffix={
                              !isMoySkladSyncedProduct ? (
                                <span style={{ color: '#ff4d4f' }}>*</span>
                              ) : undefined
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          name={['requiredCharacteristics', index, 'value']}
                          className={styles.characteristicRowField}
                          rules={isMoySkladSyncedProduct ? [] : [
                            {
                              required: true,
                              message: `Заполните ${characteristic.name}`
                            }
                          ]}
                          hasFeedback
                        >
                          {characteristic.isAnyValue ? (
                            <Input
                              size='middle'
                              placeholder={`Введите ${characteristic.name.toLowerCase()}`}
                            />
                          ) : (
                            <Select
                              size='middle'
                              showSearch
                              placeholder={`Выберите ${characteristic.name.toLowerCase()}`}
                              options={characteristic.hints.map((hint) => ({
                                value: hint,
                                label: hint
                              }))}
                            />
                          )}
                        </Form.Item>
                        <div className={styles.characteristicRowSpacer} />
                      </div>
                    </React.Fragment>
                  )
                  )}

                {productType !== EProductType.VARIATION && (
                  <Form.List name='optionalCharacteristics'>
                    {(fields) => (
                      <>
                        {fields.map((fieldData) => {
                          const characteristicName =
                            form.getFieldValue([
                              'optionalCharacteristics',
                              fieldData.name,
                              'name'
                            ]) || ''
                          const matchingChar = subCategoryCharacteristics.find(
                            (item) => item.name === characteristicName
                          )

                          return (
                            <div
                              key={`optional-${fieldData.key}`}
                              className={styles.characteristicRow}
                            >
                              <TagsOutlined className={styles.characteristicRowIcon} />
                              <Form.Item
                                name={[fieldData.name, 'name']}
                                className={styles.characteristicRowField}
                                hasFeedback
                              >
                                <Input
                                  size='middle'
                                  placeholder='Название характеристики'
                                  disabled
                                  style={{
                                    color: 'rgba(0, 0, 0, 0.45)',
                                    backgroundColor: '#f5f5f5'
                                  }}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[fieldData.name, 'value']}
                                className={styles.characteristicRowField}
                                hasFeedback
                              >
                                {matchingChar && !matchingChar.isAnyValue ? (
                                  <Select
                                    size='middle'
                                    showSearch
                                    placeholder={`Выберите ${characteristicName.toLowerCase()}`}
                                    allowClear
                                    options={matchingChar.hints.map((hint) => ({
                                      value: hint,
                                      label: hint
                                    }))}
                                  />
                                ) : (
                                  <Input
                                    size='middle'
                                    placeholder={`Введите ${characteristicName.toLowerCase()}`}
                                  />
                                )}
                              </Form.Item>
                              <div className={styles.characteristicRowSpacer} />
                            </div>
                          )
                        })}
                      </>
                    )}
                  </Form.List>
                )}

                <Form.List name='characteristics'>
                  {(fields, { remove }) => (
                    <>
                      {fields.map((fieldData) => (
                        <React.Fragment key={fieldData.key}>
                          <div
                            className={styles.characteristicRow}
                          >
                            <TagsOutlined className={styles.characteristicRowIcon} />
                            <Form.Item
                              name={[fieldData.name, 'name']}
                              className={styles.characteristicRowField}
                              rules={[
                                {
                                  required: true,
                                  message: 'Заполните название характеристики'
                                },
                                {
                                  whitespace: true,
                                  message: 'Заполните название характеристики'
                                }
                              ]}
                              hasFeedback
                            >
                              <Input
                                size='middle'
                                placeholder='Название характеристики'
                                disabled={productType === EProductType.VARIATION}
                                style={productType === EProductType.VARIATION ? {
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  backgroundColor: '#f5f5f5'
                                } : {}}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[fieldData.name, 'value']}
                              className={styles.characteristicRowField}
                              rules={[
                                {
                                  required: true,
                                  message: 'Заполните значение характеристики'
                                },
                                {
                                  whitespace: true,
                                  message: 'Заполните значение характеристики'
                                }
                              ]}
                              hasFeedback
                            >
                              <Input
                                size='middle'
                                placeholder='Значение характеристики'
                                disabled={productType === EProductType.VARIATION}
                                style={productType === EProductType.VARIATION ? {
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  backgroundColor: '#f5f5f5'
                                } : {}}
                              />
                            </Form.Item>
                            {productType !== EProductType.VARIATION ? (
                              <Button
                                type='primary'
                                htmlType='button'
                                size='middle'
                                style={{ marginTop: 4 }}
                                icon={<DeleteOutlined style={{ fontSize: 16 }} />}
                                onClick={() => remove(fieldData.name)}
                                ghost
                                danger
                              />
                            ) : (
                              <div className={styles.characteristicRowSpacer} />
                            )}
                          </div>
                        </React.Fragment>
                      )
                      )}
                    </>
                  )}
                </Form.List>
              </div>
            )}
          {productType !== EProductType.VARIATION && (
            <div className={styles.addCharacteristicsWrapper}>
              <div className={styles.addCharacteristicsInputs}>
                <div className={styles.addCharacteristicsInput}>
                  <Typography.Title
                    level={5}
                    className={styles.addCharacteristicsInputsTitle}
                  >
                  Новая характеристика
                  </Typography.Title>
                  <Form.Item
                    name='createCharacteristicName'
                    className={styles.addCharacteristicsInputsField}
                  >
                    <Input size='middle' placeholder='Название характеристики' />
                  </Form.Item>
                </div>
                <div className={styles.addCharacteristicsInput}>
                  <Typography.Title
                    level={5}
                    className={styles.addCharacteristicsInputsTitle}
                  >
                  Значение
                  </Typography.Title>
                  <Form.List
                    name='createCharacteristicValues'
                    initialValue={['']}
                  >
                    {(fields) => (
                      <>
                        {fields.map((fieldData) => (
                          <Form.Item
                            name={fieldData.name}
                            key={fieldData.key}
                            className={styles.addCharacteristicsInputsField}
                          >
                            {matchingSubCategoryChar &&
                          !matchingSubCategoryChar.isAnyValue ? (
                                <Select
                                  size='middle'
                                  showSearch
                                  placeholder={`Выберите ${matchingSubCategoryChar.name.toLowerCase()}`}
                                  options={matchingSubCategoryChar.hints.map(
                                    (hint) => ({
                                      value: hint,
                                      label: hint
                                    })
                                  )}
                                />
                              ) : (
                                <Input size='middle' placeholder='Значение' />
                              )}
                          </Form.Item>
                        ))}
                      </>
                    )}
                  </Form.List>
                </div>
              </div>
              <div>
                <div className={styles.addCharacteristicsFooterActions}>
                  <Button
                    type='primary'
                    htmlType='button'
                    icon={<ForkOutlined />}
                    size='middle'
                    disabled={
                      !createCharacteristicName.trim() ||
                      !createCharacteristicValue.trim()
                    }
                    onClick={onCreateCharacteristic}
                  >
                  Добавить
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* скрытый валидатор: при form.validateFields() проверяет что у всех строк вариаций привязан товар */}
        <Form.Item
          name='_charVariationsValidation'
          style={{ display: 'none' }}
          rules={[{
            validator: async () => {
              const errors = new Set<number>()
              const currentVariations: IFrontVariation[] =
                form.getFieldValue('variations') ?? []
              currentVariations.forEach((item, index) => {
                const hasValue =
                  item.firstCharacteristicValue?.trim() ||
                  item.secondCharacteristicValue?.trim()
                if (hasValue && !item.product) {
                  errors.add(index)
                }
              })
              setCharVariationErrors(errors)
              if (errors.size > 0) {
                throw new Error('Выберите товар для всех вариаций')
              }
            }
          }]}
        >
          <input type='hidden' />
        </Form.Item>
        <VariantProductModalForm
          data={createUpdateProductData}
          parentFormData={parentFormData}
          sellerId={sellerId}
          parentProductId={productId}
          openChooseProductModal={openChooseProductModal}
          onSelectSuccess={onSelectVariationProduct}
          onClose={closeCreateUpdateProductModal}
        />
        <ChooseVariationProductModal
          data={chooseProductData}
          variations={variations}
          sellerId={sellerId}
          productId={productId}
          parentSubCategoryId={subCategoryId}
          openCreateProductModal={openCreateProductModalForChoosing}
          openUpdateProductModal={openUpdateProductModal}
          onClose={closeChooseProductModal}
        />
      </>
    )
  }
)
